import React, { useEffect, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-hot-toast';

interface RoleGuardProps {
  children: React.ReactNode;
}

/**
 * RoleGuard component that restricts access to admin pages
 * only to users with the 'admin' role.
 * Uses cached userRole from AuthContext for optimal performance.
 */
const RoleGuard: React.FC<RoleGuardProps> = ({ children }) => {
  const { user, loading, userRole } = useAuth();
  const location = useLocation();
  const hasShownToast = useRef(false);

  // Derive isAdmin directly from cached userRole
  const isAdmin = userRole === 'admin';

  // Show toast in useEffect to avoid state updates during render
  useEffect(() => {
    if (!loading && (!user || !isAdmin) && !hasShownToast.current) {
      toast.error('You need admin privileges to access this page');
      hasShownToast.current = true;
    }
  }, [loading, user, isAdmin]);

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Redirect to home if not authenticated or not an admin
  if (!user || !isAdmin) {
    // Redirect to home page
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // Render children if authenticated and an admin
  return <>{children}</>;
};

export default RoleGuard;
